import { Feature } from "@minicity/core";

export interface CitizenInvite {
    id: string;
    partnerName: string;
    partnerEnabledModules: Record<string, Feature.Status>
    
    sites: OnboardingSite[];

    terms: InformationCard[],
    termsVersion: number;

    classifierQuestions: ClassifierQuestion[];
}

interface OnboardingSite {
    id: string;
    name: string;
    caseworkers: OnboardingCaseworker[];
}

interface OnboardingCaseworker {
    id: string;
    name: string;
    ambassadors?: string[];
}

export interface InformationCard {
    label: string,
    description?: string,
    color?: string,
    url: string
}

export enum ClassifierQuestionType { CHOOSE_ONE = "CHOOSE_ONE", CHOOSE_MULTI = "CHOOSE_MULTI" }

export interface ClassifierQuestion {
    id: string;
    prompt: string;
    answers: {id: string, label: string}[];

    type: ClassifierQuestionType;
    
    includeOtherOption?: boolean;
    otherOptionLabel?: string;
}

export interface HmisClassifiers {
    livingPlace: string|null,
    livingDuration: string|null,
    homelessnessStart: {
      status: string|null,
      dateStarted: string,
    },
    timesOnTheStreet: string|null,
    monthsOnTheStreet: string|null,
    healthInsurance: string[],
    barriers: {
      'Alcohol use':                  {hasBarrier: boolean, stillPresent: boolean},
      'Drug use':                     {hasBarrier: boolean, stillPresent: boolean},
      'Chronic health condition':     {hasBarrier: boolean, stillPresent: boolean},
      'Physical disability':          {hasBarrier: boolean, stillPresent: boolean},
      'Developmental disability':     {hasBarrier: boolean, stillPresent: boolean},
      'HIV / AIDS':                   {hasBarrier: boolean, stillPresent: boolean},
      'Mental health':                {hasBarrier: boolean, stillPresent: boolean},
    },
    domesticViolence: string|null,
    veteran: string|null,
    income: {
      status: string|null,
      categories: {
        'Earned income (i.e. employment income)': {hasIncome: boolean, description: string, amount: string},
        'Unemployment insurance':                 {hasIncome: boolean, description: string, amount: string},
        'Supplemental Security Income (SSI)':     {hasIncome: boolean, description: string, amount: string},
        'Social Security Disability Insurance':   {hasIncome: boolean, description: string, amount: string},
        'Veteran\'s Disability Payment':          {hasIncome: boolean, description: string, amount: string},
        'Private Disability Insurance':           {hasIncome: boolean, description: string, amount: string},
        'Worker\'s Compensation':                 {hasIncome: boolean, description: string, amount: string},
        'TANF':                                   {hasIncome: boolean, description: string, amount: string},
        'General Assistance':                     {hasIncome: boolean, description: string, amount: string},
        'Retirement income from Social Security': {hasIncome: boolean, description: string, amount: string},
        'Veteran\'s Pension':                     {hasIncome: boolean, description: string, amount: string},
        'Other Pension':                          {hasIncome: boolean, description: string, amount: string},
        'Child Support':                          {hasIncome: boolean, description: string, amount: string},
        'Alimony or other spousal support':       {hasIncome: boolean, description: string, amount: string},
        'Other income':                           {hasIncome: boolean, description: string, amount: string},
      }
    },
    benefits: {
      status: string|null,
      description: string,
    },
    expenses: {
      status: string|null,
      categories: {
        'Out-of-Pocket Medical Expenses': {hasExpense: boolean, description: string, amount: string},
        'Rent':                           {hasExpense: boolean, description: string, amount: string},
        'Security Deposit':               {hasExpense: boolean, description: string, amount: string},
        'Mortgage':                       {hasExpense: boolean, description: string, amount: string},
        'Utilities - Electric':           {hasExpense: boolean, description: string, amount: string},
        'Utilities - Gas':                {hasExpense: boolean, description: string, amount: string},
        'Utilities - Water':              {hasExpense: boolean, description: string, amount: string},
        'Maintenance':                    {hasExpense: boolean, description: string, amount: string},
        'Car Payment':                    {hasExpense: boolean, description: string, amount: string},
        'Car Insurance':                  {hasExpense: boolean, description: string, amount: string},
        'Groceries':                      {hasExpense: boolean, description: string, amount: string},
        'Gasoline':                       {hasExpense: boolean, description: string, amount: string},
        'Miscellaneous':                  {hasExpense: boolean, description: string, amount: string},
      }
    },
    consentToShare: string|null,
}

export const validateAndFlattenHmisClassifiers = (c: HmisClassifiers) => {
    const flattened:Partial<{[key in keyof HmisClassifiers]: string[]}> = {};

    if (c.livingPlace == null) throw new Error('Please answer "Where did you stay last night?"');
    flattened.livingPlace = [c.livingPlace];

    if (c.livingDuration == null) throw new Error ('Please answer "How long have you been staying there?"');
    flattened.livingDuration = [c.livingDuration];

    if (c.homelessnessStart.status == null) throw new Error ('Please answer "When did your homelessness start?"');
    if (c.homelessnessStart.status == 'Enter date') {
        if (c.homelessnessStart.dateStarted.length == 0) throw new Error ('Please enter the date for question "When did your homelessness start?"');
        flattened.homelessnessStart = [`Date: ${c.homelessnessStart.dateStarted}`];
    } else {
        flattened.homelessnessStart = [c.homelessnessStart.status];
    }

    if (c.timesOnTheStreet == null) throw new Error ('Please answer "How many times have you stayed on the street, or in a shelter, in the past 3 years?"');
    flattened.timesOnTheStreet = [c.timesOnTheStreet];

    if (c.monthsOnTheStreet == null) throw new Error ('Please answer "For how many months in total have you been on the street in the past 3 years?"');
    flattened.monthsOnTheStreet = [c.monthsOnTheStreet];

    if (c.healthInsurance.length == 0) throw new Error ('Please answer "Do you receive health insurance from any of the following?"');
    flattened.healthInsurance = c.healthInsurance;

    flattened.barriers = [];
    for (const [barrierName, barrier] of Object.entries(c.barriers)) {
        if (barrier.hasBarrier) {
            flattened.barriers.push(barrier.stillPresent ? `${barrierName} (Still Present)` : barrierName);
        }
    }

    if (c.domesticViolence == null) throw new Error ('Please answer "Are you a survivor of domestic violence?"');
    flattened.domesticViolence = [c.domesticViolence];

    if (c.veteran == null) throw new Error ('Please answer "Are you a veteran, or in active military service?"');
    flattened.veteran = [c.veteran];

    if (c.income.status == null) throw new Error ('Please answer "Do you currently have monthly income, from any source?"');
    if (c.income.status == 'YES') {
        flattened.income = [];
        for (const [incomeName, income] of Object.entries(c.income.categories)) {
            if (income.hasIncome) {
                if (income.description.length == 0 || income.amount.length == 0) throw new Error (`Please provide details for "${incomeName}" income`);
                flattened.income.push(`${incomeName} (${income.description}): $${income.amount}`);
            }
        }
    } else {
        flattened.income = ["None"];
    }

    if (c.benefits.status == null) throw new Error ('Please answer "Do you receive any non-cash benefits?"');
    if (c.benefits.status == 'Yes') {
        if (c.benefits.description.length == 0) throw new Error (`Please provide details for non-cash benefits received`);
        flattened.benefits = [`Yes: ${c.benefits.description}`];
    } else {
        flattened.benefits = [c.benefits.status];
    }

    if (c.expenses.status == null) throw new Error ('Please answer "Do you currently have monthly expenses, from any source?"');
    if (c.expenses.status == 'YES') {
        flattened.expenses = [];
        for (const [expenseName, expense] of Object.entries(c.expenses.categories)) {
            if (expense.hasExpense) {
                if (expense.description.length == 0 || expense.amount.length == 0) throw new Error (`Please provide details for "${expenseName}" expense`);
                flattened.expenses.push(`${expenseName} (${expense.description}): $${expense.amount}`);
            }
        }
    } else {
        flattened.expenses = ["None"];
    }

    if (c.consentToShare == null) throw new Error ('Please answer "Do you consent to share this information?"');
    flattened.consentToShare = [c.consentToShare];

    return flattened;
}